import { useQuery } from '@tanstack/react-query';
import { DICTATION_POST_PROCESSING_VARIANT_QUERY_KEY } from '../../contracts/dictation-feedback-query-keys';
import {
  DICTATION_FEEDBACK_EXPERIMENT_VARIANTS,
  type DictationFeedbackExperimentVariant,
} from '../../contracts/dictation-feedback.types';
import { t11nHttp } from '../api/t11nHttp';

export { DICTATION_POST_PROCESSING_VARIANT_QUERY_KEY } from '../../contracts/dictation-feedback-query-keys';

type DictationPostProcessingVariantWireResponse = {
  variant: string;
};

export type DictationPostProcessingVariantResponse = {
  variant: DictationFeedbackExperimentVariant;
};

export type UseQueryDictationPostProcessingVariantProps = {
  isDesktop: boolean;
  outputLanguages: string[] | undefined;
  enabled: boolean;
};

const isDictationFeedbackExperimentVariant = (
  variant: string
): variant is DictationFeedbackExperimentVariant =>
  (DICTATION_FEEDBACK_EXPERIMENT_VARIANTS as readonly string[]).includes(variant);

const getDictationPostProcessingVariant = async ({
  isDesktop,
  outputLanguages,
}: Pick<
  UseQueryDictationPostProcessingVariantProps,
  'isDesktop' | 'outputLanguages'
>): Promise<DictationPostProcessingVariantResponse | null> => {
  const query = new URLSearchParams({ is_desktop: String(isDesktop) });
  for (const language of outputLanguages ?? []) {
    query.append('output_languages', language);
  }

  const { data } = await t11nHttp.get<DictationPostProcessingVariantWireResponse>(
    `/dictation-post-processing-variant?${query.toString()}`
  );

  return isDictationFeedbackExperimentVariant(data.variant)
    ? { variant: data.variant }
    : null;
};

/**
 * Reads the already-safe experiment label used by Dictate. It is a cohort
 * label, never the underlying post-processing model name.
 */
export function useQueryDictationPostProcessingVariant({
  isDesktop,
  outputLanguages,
  enabled,
}: UseQueryDictationPostProcessingVariantProps) {
  const hasOutputLanguages = (outputLanguages?.length ?? 0) > 0;

  return useQuery({
    queryKey: [
      DICTATION_POST_PROCESSING_VARIANT_QUERY_KEY,
      { isDesktop, outputLanguages },
    ],
    queryFn: () => getDictationPostProcessingVariant({ isDesktop, outputLanguages }),
    staleTime: 5 * 60 * 1000,
    enabled: enabled && hasOutputLanguages,
  });
}
